import type {
  VizDataset,
  VizField,
  VizMetricAggregation,
} from "@/lib/ai/visualization/data/schema";

export type RatioMetricDependency = {
  numeratorKey: string;
  denominatorKey: string;
  offset?: number;
  zeroDenominatorValue?: number | null;
};

function isDurationUnitOrFormat(field: {
  unit?: string;
  format?: VizField["format"];
}): boolean {
  return field.unit === "duration" || field.format === "duration";
}

function isRankUnit(field: { unit?: string }): boolean {
  return field.unit === "rank";
}

function isPercentUnitOrFormat(field: {
  unit?: string;
  format?: VizField["format"];
}): boolean {
  return field.unit === "percent" || field.format === "percent";
}

/**
 * `unit/format` から既定の集計意味論を決定する。
 *
 * @remarks
 * 後方互換を持たない新契約では、metric field は `aggregation` を明示するのが原則。
 * 既定では domain 知識を注入せず、`percent/rank/duration` は非集計として扱う。
 */
export function resolveDefaultMetricAggregation(args: {
  unit?: string;
  format?: VizField["format"];
}): VizMetricAggregation {
  if (isPercentUnitOrFormat({ unit: args.unit, format: args.format })) {
    return {
      kind: "non-aggregatable",
      reason: "other",
    };
  }
  if (isRankUnit({ unit: args.unit })) {
    return {
      kind: "non-aggregatable",
      reason: "rank",
    };
  }
  if (isDurationUnitOrFormat({ unit: args.unit, format: args.format })) {
    return {
      kind: "non-aggregatable",
      reason: "duration",
    };
  }
  return {
    kind: "additive",
  };
}

/**
 * field から集計意味論を取得する。
 *
 * @remarks
 * metric でない field は `null` を返す。
 */
export function resolveMetricAggregationForField(
  field: VizField,
): VizMetricAggregation | null {
  if (!isNumericMetricField(field)) return null;
  if (field.aggregation) return field.aggregation;
  return resolveDefaultMetricAggregation({
    unit: field.unit,
    format: field.format,
  });
}

/**
 * 可視化 dataset 上で metric として扱える数値列かを判定する。
 */
export function isNumericMetricField(field: VizField): boolean {
  return field.role === "metric" || field.type === "number";
}

/**
 * render 集計で ratio-based formula として再計算できる metric かを判定する。
 *
 * @remarks
 * percent だけでなく、rank のような `Σ分子 / Σ分母 + offset` も含む。
 */
export function isRatioMetricField(field: VizField): boolean {
  return resolveMetricAggregationForField(field)?.kind === "ratio";
}

/**
 * bar 集計で再計算不能な metric かを判定する。
 */
export function isUnsupportedAggregatedMetricField(field: VizField): boolean {
  return resolveMetricAggregationForField(field)?.kind === "non-aggregatable";
}

function hasNumericMetricField(
  dataset: VizDataset,
  fieldName: string,
): boolean {
  const field = dataset.fields.find((entry) => entry.name === fieldName);
  if (!field) return false;
  return isNumericMetricField(field);
}

/**
 * dataset 内で実際に利用可能な ratio 依存キーを返す。
 */
export function resolveRatioDependencyForDataset(args: {
  dataset: VizDataset;
  metricKey: string;
}): RatioMetricDependency | null {
  const field = args.dataset.fields.find(
    (entry) => entry.name === args.metricKey,
  );
  if (!field) return null;
  const semantics = resolveMetricAggregationForField(field);
  if (!semantics || semantics.kind !== "ratio") return null;
  const dependency = {
    numeratorKey: semantics.numeratorKey,
    denominatorKey: semantics.denominatorKey,
    ...(semantics.offset !== undefined ? { offset: semantics.offset } : null),
    ...("zeroDenominatorValue" in semantics
      ? { zeroDenominatorValue: semantics.zeroDenominatorValue }
      : null),
  };
  if (
    !hasNumericMetricField(args.dataset, dependency.numeratorKey) ||
    !hasNumericMetricField(args.dataset, dependency.denominatorKey)
  ) {
    return null;
  }
  return dependency;
}
